"use client";

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  type CSSProperties,
  type FormEvent,
  type KeyboardEventHandler,
} from "react";

import { useSessionRouteStore } from "@/app/session/SessionRouteStoreProvider";
import { Input } from "@/components/ui/input";
import { Check, Loader2, Search, Plus } from "@/components/ui/icons";
import {
  useMediaPlaylistTracks,
  type MediaPlaylistTrackItem,
} from "@/hooks/useMediaLibrary";
import { startSpotifyPlaybackOnDevice } from "@/hooks/spotifyWebPlayer.client";
import { resolveSpotifyPlaybackDeviceId } from "@/hooks/spotifyWebPlayer.shared";
import { useSpotifyWebPlayer } from "@/hooks/useSpotifyWebPlayer";
import { useSessionMediaProvider } from "@/hooks/useSessionMediaProvider";
import { useStore } from "@/store";
import { cn } from "@/lib/utils";
import { getSensorState, subscribeSensorState } from "@/lib/sensorStreamHub";
import { withAdminSessionHeaders } from "@/lib/adminSession";
import {
  fetchMediaProviderJson,
  isMediaProviderRequestError,
  mediaProviderErrorMessage,
  normalizeMediaSearchTracks,
  type MediaProviderSearchTrack as SearchTrack,
} from "@/lib/mediaProviderClient";
import {
  dispatchSessionMediaTrackPlay,
  scheduleSessionMediaPlaylistRefresh,
} from "@/lib/sessionMediaProvider";

type MediaSearchBarProps = {
  autoFocus?: boolean;
  inlineResults?: boolean;
  compact?: boolean;
  onCloseMenus?: () => void;
};

const desktopSearchFormStyle = {
  borderColor:
    "color-mix(in srgb, var(--session-accent-border) 54%, var(--surface-border) 46%)",
  background:
    "color-mix(in srgb, var(--surface-card) 98%, black 2%)",
  boxShadow:
    "0 2px 8px color-mix(in srgb, black 8%, transparent)",
  backdropFilter: "none",
} satisfies CSSProperties;

const compactSearchFormStyle = {
  borderColor:
    "color-mix(in srgb, var(--session-accent-border) 70%, var(--surface-border) 30%)",
  background:
    "color-mix(in srgb, var(--surface-card) 94%, black 6%)",
  boxShadow:
    "0 16px 32px color-mix(in srgb, black 28%, transparent)",
  backdropFilter: "none",
} satisfies CSSProperties;

const dropdownCardStyle = {
  borderColor:
    "color-mix(in srgb, var(--session-accent-border) 68%, var(--surface-border) 32%)",
  background:
    "color-mix(in srgb, var(--surface-panel) 96%, black 4%)",
  boxShadow:
    "0 24px 44px color-mix(in srgb, black 34%, transparent)",
  backdropFilter: "none",
} satisfies CSSProperties;

function normalizeLookupValue(value: string | null | undefined) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function extractLookupIdFromUri(uri: string | null | undefined) {
  const normalized = normalizeLookupValue(uri);
  if (!normalized) return null;

  if (normalized.includes(":")) {
    const segments = normalized.split(":").filter(Boolean);
    return segments[segments.length - 1] ?? null;
  }

  const withoutQuery = normalized.split("?")[0] ?? normalized;
  const pathSegments = withoutQuery.split("/").filter(Boolean);
  return pathSegments[pathSegments.length - 1] ?? null;
}

function collectTrackLookupKeys(...values: Array<string | null | undefined>) {
  const keys = new Set<string>();

  for (const value of values) {
    const normalized = normalizeLookupValue(value);
    if (normalized) {
      keys.add(normalized);
    }
  }

  return Array.from(keys);
}

function getSearchTrackLookupKeys(track: SearchTrack) {
  return collectTrackLookupKeys(
    track.id,
    track.uri,
    extractLookupIdFromUri(track.uri),
  );
}

function getPlaylistTrackLookupKeys(item: MediaPlaylistTrackItem) {
  return collectTrackLookupKeys(
    item.track?.id,
    item.track?.uri,
    item.uri,
    extractLookupIdFromUri(item.track?.uri),
    extractLookupIdFromUri(item.uri),
  );
}

/**
 * Media search bar with a dropdown of results.
 */
export function MediaSearchBar({
  autoFocus = false,
  inlineResults = false,
  compact = false,
}: MediaSearchBarProps = {}) {
  const setPlaylistSummaryView = useSessionRouteStore(
    (state) => state.setPlaylistSummaryView
  );
  const {
    adapter,
    label: providerLabel,
    isSpotifyProvider,
    isYouTubeProvider,
    isAppleMusicProvider,
  } = useSessionMediaProvider();
  const supportsMediaSearch = adapter?.supportsMediaSearch ?? false;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState<{
    type: "error" | "success";
    text: string;
    action?: { label: string; href: string };
  } | null>(null);
  const [actionTrackId, setActionTrackId] = useState<string | null>(null);
  const [activeTrackLookupKeys, setActiveTrackLookupKeys] = useState<string[]>(
    [],
  );
  const [isSensorConnected, setIsSensorConnected] = useState(
    () => getSensorState().isConnected
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectedPlaylist = useSessionRouteStore(
    (state) => state.selectedPlaylist
  );
  const playlistRefreshToken = useSessionRouteStore(
    (state) => state.playlistRefreshToken
  );
  const bumpPlaylistRefresh = useSessionRouteStore(
    (state) => state.bumpPlaylistRefresh
  );
  const { tracks: selectedPlaylistTracks } = useMediaPlaylistTracks(
    selectedPlaylist?.id ?? null,
    playlistRefreshToken,
  );
  const [optimisticPlaylistTrackKeysByPlaylist, setOptimisticPlaylistTrackKeysByPlaylist] =
    useState<Record<string, string[]>>({});
  const userSpotifyId = useStore.use.user()?.spotify_id ?? null;
  const selectedPlaylistServerTrackKeys = new Set<string>();

  for (const item of selectedPlaylistTracks) {
    for (const key of getPlaylistTrackLookupKeys(item)) {
      selectedPlaylistServerTrackKeys.add(key);
    }
  }

  const selectedPlaylistOptimisticTrackKeys = selectedPlaylist
    ? optimisticPlaylistTrackKeysByPlaylist[selectedPlaylist.id] ?? []
    : [];
  const selectedPlaylistTrackKeys = new Set(selectedPlaylistServerTrackKeys);

  for (const key of selectedPlaylistOptimisticTrackKeys) {
    selectedPlaylistTrackKeys.add(key);
  }

  const canEditSelectedPlaylist =
    isSpotifyProvider &&
    !!selectedPlaylist &&
    (selectedPlaylist.collaborative ||
      (!!userSpotifyId && selectedPlaylist.ownerId === userSpotifyId));
  const addRequiresManagedPlaylist =
    adapter?.playlists.addRequiresManagedPlaylist ?? false;
  const canAddToSelectedPlaylist =
    Boolean(selectedPlaylist) &&
    Boolean(adapter) &&
    (!addRequiresManagedPlaylist || canEditSelectedPlaylist);
  const {
    isReady,
    deviceId,
    sharedCurrentTrack,
    togglePlay,
    ensureReady,
    error: playerError,
  } = useSpotifyWebPlayer({ autoTransfer: false });
  const sharedCurrentTrackLookupKeys = collectTrackLookupKeys(
    sharedCurrentTrack?.id,
    sharedCurrentTrack?.uri,
    extractLookupIdFromUri(sharedCurrentTrack?.uri),
  );
  const effectiveCurrentTrackLookupKeys =
    sharedCurrentTrackLookupKeys.length > 0
      ? sharedCurrentTrackLookupKeys
      : activeTrackLookupKeys;

  const SEARCH_LIMIT = 15;
  const playbackBlockedBySensor = supportsMediaSearch && !isSensorConnected;

  const markTrackAsPresentInPlaylist = useCallback(
    (playlistId: string, track: SearchTrack) => {
      const trackKeys = getSearchTrackLookupKeys(track);
      if (trackKeys.length === 0) return;

      setOptimisticPlaylistTrackKeysByPlaylist((prev) => {
        const nextKeys = new Set(prev[playlistId] ?? []);
        let changed = false;

        for (const key of trackKeys) {
          if (!nextKeys.has(key)) {
            nextKeys.add(key);
            changed = true;
          }
        }

        if (!changed) return prev;
        return {
          ...prev,
          [playlistId]: Array.from(nextKeys),
        };
      });
    },
    [],
  );

  const schedulePostAddRefresh = useCallback(() => {
    scheduleSessionMediaPlaylistRefresh(adapter, bumpPlaylistRefresh);
  }, [adapter, bumpPlaylistRefresh]);

  useEffect(() => {
    const playlistId = selectedPlaylist?.id;
    if (!playlistId) return;
    const serverTrackKeys = new Set<string>();

    for (const item of selectedPlaylistTracks) {
      for (const key of getPlaylistTrackLookupKeys(item)) {
        serverTrackKeys.add(key);
      }
    }

    setOptimisticPlaylistTrackKeysByPlaylist((prev) => {
      const optimisticKeys = prev[playlistId];
      if (!optimisticKeys || optimisticKeys.length === 0) return prev;

      const remainingKeys = optimisticKeys.filter((key) => !serverTrackKeys.has(key));

      if (remainingKeys.length === optimisticKeys.length) {
        return prev;
      }

      if (remainingKeys.length === 0) {
        const nextState = { ...prev };
        delete nextState[playlistId];
        return nextState;
      }

      return {
        ...prev,
        [playlistId]: remainingKeys,
      };
    });
  }, [selectedPlaylist?.id, selectedPlaylistTracks]);

  /**
   * Run search via the active provider's search endpoint. Result set is
   * intentionally small for a dropdown: SEARCH_LIMIT.
   */
  const runSearch = useCallback(async (q: string) => {
    if (!adapter || !adapter.supportsMediaSearch) {
      setResults([]);
      setError("Search is not available for this login provider.");
      setActionMessage(null);
      setOpen(true);
      return;
    }

    const trimmed = q.trim();

    if (!trimmed) {
      setResults([]);
      setError(null);
      setActionMessage(null);
      setOpen(false);
      return;
    }

    setLoading(true);
    setError(null);
    setActionMessage(null);
    setOpen(true);

    try {
      const params = new URLSearchParams({
        q: trimmed,
        type: "track",
        limit: String(SEARCH_LIMIT),
      });

      const endpoint = adapter.search.endpoint(params);
      const data = await fetchMediaProviderJson<unknown>(
        endpoint,
        {},
        "Search failed",
      );
      setResults(normalizeMediaSearchTracks(data));
    } catch (err: unknown) {
      console.error("[MediaSearch] Error", err);
      if (
        isMediaProviderRequestError(err) &&
        err.status === 409 &&
        err.responseText.includes("SESSION_PROVIDER_MISMATCH")
      ) {
          setError(`Search is only available for ${providerLabel} sessions.`);
          setResults([]);
          return;
      }
      setError(mediaProviderErrorMessage(err, "Search failed"));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [adapter, providerLabel]);

  /**
   * Submit handler (Enter key or Search button).
   */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void runSearch(query);
  };

  useEffect(() => {
    return subscribeSensorState((next) => {
      setIsSensorConnected(next.isConnected);
    });
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void runSearch(query);
    }, 220);

    return () => window.clearTimeout(timeout);
  }, [query, runSearch]);

  /**
   * When a track is selected, start playback in the main transport.
   */
  const handlePlayTrack = async (track: SearchTrack) => {
    setActionMessage(null);
    if (!adapter) {
      setActionMessage({
        type: "error",
        text: "Playback is unavailable for this provider.",
      });
      return;
    }
    if (isYouTubeProvider && track.is_restricted) {
      setActionMessage({
        type: "error",
        text: `This video is restricted${track.restriction_reason ? ` (${track.restriction_reason})` : ""} and cannot be played.`,
      });
      return;
    }
    if (playbackBlockedBySensor) {
      setActionMessage({
        type: "error",
        text: "Sensor disconnected. Reconnect to start playback.",
      });
      return;
    }

    if (adapter.search.playbackDispatchMode === "store_action") {
      setActiveTrackLookupKeys(getSearchTrackLookupKeys(track));
      const queueUris =
        adapter.search.queueMode === "search_results"
          ? results
              .map((result) =>
                typeof result.uri === "string" ? result.uri.trim() : "",
              )
              .filter((uri) => uri.length > 0)
          : [];
      const queueIndex = queueUris.findIndex((uri) => uri === track.uri);
      dispatchSessionMediaTrackPlay(adapter, track, {
        queueUris: queueIndex >= 0 ? queueUris : [],
        queueIndex: queueIndex >= 0 ? queueIndex : undefined,
        queueSource: queueIndex >= 0 ? "search_results" : null,
      });
      setPlaylistSummaryView("track");
      return;
    }

    if (sharedCurrentTrack?.uri === track.uri) {
      try {
        await togglePlay();
        setActiveTrackLookupKeys(getSearchTrackLookupKeys(track));
        setPlaylistSummaryView("track");
      } catch (err: unknown) {
        setActionMessage({
          type: "error",
          text: mediaProviderErrorMessage(err, "Failed to toggle playback"),
        });
      }
      return;
    }

    setActionTrackId(track.id);
    try {
      const playbackDeviceId = await resolveSpotifyPlaybackDeviceId({
        isReady,
        deviceId,
        ensureReady,
        playerError,
      });
      const result = await startSpotifyPlaybackOnDevice({
        deviceId: playbackDeviceId,
        uris: [track.uri],
      });
      if (!result.ok) {
        throw new Error(result.errorMessage ?? "Failed to start playback");
      }
      setActiveTrackLookupKeys(getSearchTrackLookupKeys(track));
      setPlaylistSummaryView("track");
    } catch (err: unknown) {
      setActionMessage({
        type: "error",
        text: mediaProviderErrorMessage(err, "Failed to start playback"),
      });
    } finally {
      setActionTrackId(null);
    }
  };

  const handleAddToPlaylist = async (track: SearchTrack) => {
    setActionMessage(null);
    if (!adapter) {
      setActionMessage({
        type: "error",
        text: "Adding from search is unavailable for this provider.",
      });
      return;
    }
    if (isYouTubeProvider && track.is_restricted) {
      setActionMessage({
        type: "error",
        text: `This video is restricted${track.restriction_reason ? ` (${track.restriction_reason})` : ""} and cannot be added.`,
      });
      return;
    }

    if (!adapter.supportsMediaSearch) {
      setActionMessage({
        type: "error",
        text: "Adding from search is unavailable for this provider.",
      });
      return;
    }

    if (!selectedPlaylist) {
      setActionMessage({
        type: "error",
        text: "Select a playlist to add tracks.",
      });
      return;
    }

    if (isSpotifyProvider && !canEditSelectedPlaylist) {
      setActionMessage({
        type: "error",
        text:
          "This playlist is read-only. Choose one you own or a collaborative playlist.",
      });
      return;
    }

    const selectedPlaylistId = selectedPlaylist.id;
    const selectedPlaylistName = selectedPlaylist.name;

    setActionTrackId(track.id);
    try {
      const endpoint = adapter.playlists.mutationEndpoint(selectedPlaylistId);
      const res = await fetch(endpoint, withAdminSessionHeaders({
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uris: [track.uri],
        }),
      }));

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let message = text;
        let responseCode = "";
        if (text) {
          try {
            const parsed = JSON.parse(text);
            message =
              parsed?.error?.message || parsed?.error || text;
            responseCode =
              typeof parsed?.code === "string" ? parsed.code : "";
          } catch {
            // Keep original text when it isn't JSON.
          }
        }
        const duplicateTrack =
          res.status === 409 && responseCode === "TRACK_ALREADY_EXISTS";
        if (duplicateTrack) {
          const duplicateError = new Error(
            typeof message === "string" && message
              ? message
              : "This track is already in the selected playlist."
          ) as Error & { code?: string };
          duplicateError.code = "TRACK_ALREADY_EXISTS";
          throw duplicateError;
        }
        const missingSpotifyScope =
          res.status === 403 &&
          typeof message === "string" &&
          message.toLowerCase().includes("insufficient client scope");
        if (missingSpotifyScope) {
          const scopeError = new Error(
            "Can't add to this playlist. Choose one you own or collaborative, or reauthorize Spotify to grant playlist permissions."
          ) as Error & { code?: string };
          scopeError.code = "MISSING_PLAYLIST_SCOPE";
          throw scopeError;
        }
        const missingYouTubeScope =
          isYouTubeProvider &&
          res.status === 403 &&
          typeof message === "string" &&
          message.toLowerCase().includes("missing youtube write scope");
        if (missingYouTubeScope) {
          const scopeError = new Error(
            "YouTube add-to-playlist needs write scope. Reauthorize YouTube and try again."
          ) as Error & { code?: string };
          scopeError.code = "MISSING_YOUTUBE_WRITE_SCOPE";
          throw scopeError;
        }
        throw new Error(message || `Failed to add track (${res.status})`);
      }

      markTrackAsPresentInPlaylist(selectedPlaylistId, track);
      schedulePostAddRefresh();
      setActionMessage({
        type: "success",
        text:
          adapter.key === "spotify"
            ? `Added to ${selectedPlaylistName}.`
            : `Added to ${providerLabel} playlist ${selectedPlaylistName}.`,
      });
    } catch (err: unknown) {
      const code =
        typeof err === "object" && err && "code" in err
          ? String((err as { code?: unknown }).code ?? "")
          : "";
      const missingScope = code === "MISSING_PLAYLIST_SCOPE";
      const missingYouTubeWriteScope =
        code === "MISSING_YOUTUBE_WRITE_SCOPE";
      const duplicateTrack = code === "TRACK_ALREADY_EXISTS";
      if (duplicateTrack) {
        markTrackAsPresentInPlaylist(selectedPlaylistId, track);
      }
      setActionMessage({
        type: "error",
        text: duplicateTrack
          ? `${track.name} is already in ${selectedPlaylistName}.`
          : mediaProviderErrorMessage(err, "Failed to add track"),
        action: missingScope
          ? {
              label: "Reauthorize Spotify",
              href: `/api/spotify/login?force=1&returnTo=${encodeURIComponent(
                "/presurvey"
              )}`,
            }
          : missingYouTubeWriteScope
            ? {
                label: "Reauthorize YouTube",
                href: `/api/youtube/login?force=1&returnTo=${encodeURIComponent(
                  "/presurvey"
                )}`,
            }
          : undefined,
      });
    } finally {
      setActionTrackId(null);
    }
  };

  /**
   * Close dropdown when clicking outside of the component.
   */
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!autoFocus) return;
    const timeout = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(timeout);
  }, [autoFocus]);

  /**
   * Handle Escape key: close dropdown and blur input.
   */
  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActionMessage(null);
      (e.target as HTMLInputElement).blur();
    }
  };

  const shouldShowDropdown =
    open && (results.length > 0 || loading || error !== null);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-full min-w-0",
        compact ? "max-w-none" : "mx-auto max-w-[32rem]"
      )}
    >
      <form
        onSubmit={handleSubmit}
        className={cn(
          "flex items-center gap-2 rounded-[1.05rem] border p-1",
          compact ? "rounded-2xl" : null
        )}
        style={compact ? compactSearchFormStyle : desktopSearchFormStyle}
      >
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:color-mix(in_oklch,var(--color-muted-foreground)_70%,transparent)]" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search tracks or artists…"
            className="h-10 border-0 bg-transparent pl-9 pr-9 text-base shadow-none focus-visible:border-transparent focus-visible:shadow-none"
          />
          {loading && (
            <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[color:color-mix(in_oklch,var(--page-bg-to)_60%,var(--page-bg-from)_40%)] motion-reduce:animate-none" />
          )}
        </div>
      </form>

      {shouldShowDropdown && (
        <div
          className={cn(
            inlineResults
              ? "mt-2 w-full max-w-full"
              : "absolute left-1/2 top-full z-50 mt-2 w-full max-w-[calc(100vw-1.5rem)] -translate-x-1/2 md:max-w-[calc(100vw-2rem)]"
          )}
        >
          <div
            className="max-h-[72vh] w-full max-w-full overflow-hidden rounded-[1.25rem] border"
            style={dropdownCardStyle}
          >
            <div
              className="flex items-center justify-between gap-3 border-b px-2 py-1.5 text-[0.6875rem] font-semibold text-[color:var(--color-muted-foreground)]"
              style={{
                borderColor:
                  "color-mix(in oklch, var(--color-border) 55%, transparent)",
              }}
            >
              <div className="flex w-full items-center justify-between gap-3">
                <span>Search Results</span>
                {results.length > 0 && (
                  <span className="text-[0.625rem] text-[color:color-mix(in_oklch,var(--color-muted-foreground)_70%,transparent)]">
                    {results.length} track{results.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>

            {playbackBlockedBySensor ? (
              <div className="border-b border-[color:var(--color-error-border)] bg-[color:var(--color-error-soft)] px-2 py-1.5 text-[0.6875rem] text-[color:var(--color-error-foreground)]">
                Sensor disconnected. Playback is disabled until reconnect.
              </div>
            ) : null}

            <div
              className={cn(
                "overflow-x-hidden overflow-y-auto",
                inlineResults
                  ? "max-h-[min(52dvh,20rem)]"
                  : "h-64 md:h-72"
              )}
            >
              <div className="py-0.5">
                  {loading && (
                    <div className="px-2 py-3 text-[0.75rem] text-[color:var(--color-muted-foreground)]">
                      Searching {providerLabel}...
                    </div>
                  )}

                  {!loading && error && (
                    <div className="px-2 py-3 text-[0.75rem] text-[color:color-mix(in_oklch,var(--color-destructive)_76%,black_24%)]">
                      {error}
                    </div>
                  )}

                  {!loading && !error && actionMessage && (
                    <div
                      className={cn(
                        "flex flex-wrap items-center gap-2 px-2 py-2 text-[0.75rem]",
                        actionMessage.type === "error"
                          ? "text-[color:color-mix(in_oklch,var(--color-destructive)_76%,black_24%)]"
                          : "text-[color:var(--color-success-foreground)]"
                      )}
                    >
                      <span>{actionMessage.text}</span>
                      {actionMessage.action && (
                        <a
                          href={actionMessage.action.href}
                          className="text-[0.625rem] font-semibold underline underline-offset-2 text-[color:color-mix(in_oklch,var(--page-bg-to)_54%,var(--page-bg-from)_46%)]"
                        >
                          {actionMessage.action.label}
                        </a>
                      )}
                    </div>
                  )}

                  {!loading && !error && results.length === 0 && (
                    <div className="px-2 py-3 text-[0.75rem] text-[color:var(--color-muted-foreground)]">
                      No tracks found for “{query.trim()}”.
                    </div>
                  )}

                  {!loading &&
                    !error &&
                    results.map((track) => {
                      const isBusy = actionTrackId === track.id;
                      const isCurrent = getSearchTrackLookupKeys(track).some(
                        (key) => effectiveCurrentTrackLookupKeys.includes(key),
                      );
                      const isAlreadyInSelectedPlaylist =
                        selectedPlaylist != null &&
                        getSearchTrackLookupKeys(track).some((key) =>
                          selectedPlaylistTrackKeys.has(key),
                        );
                      const isRestrictedTrack =
                        isYouTubeProvider && track.is_restricted;
                      const restrictionReason =
                        track.restriction_reason || "Restricted";

                      return (
                        <div
                          key={track.id}
                          className={cn(
                            "flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden rounded-[0.9rem] px-2 py-2 text-left transition-[background-color,box-shadow] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)]",
                            isCurrent
                              ? "bg-[color:color-mix(in_srgb,var(--session-accent-soft)_86%,var(--surface-card)_14%)] shadow-[inset_0_0_0_1px_var(--session-accent-border)]"
                              : "hover:bg-[color:var(--session-accent-soft)]",
                          )}
                        >
                          {isSpotifyProvider ||
                          isYouTubeProvider ||
                          isAppleMusicProvider ? (
                            isAlreadyInSelectedPlaylist ? (
                              <span
                                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[color:var(--color-success-border)] bg-[color:var(--color-success-soft)] text-[color:var(--color-success-foreground)]"
                                role="img"
                                aria-label={`Already in ${selectedPlaylist.name}`}
                                title={
                                  selectedPlaylist
                                    ? `${track.name} is already in ${selectedPlaylist.name}`
                                    : "Already in playlist"
                                }
                              >
                                <Check
                                  className="h-[0.875rem] w-[0.875rem]"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleAddToPlaylist(track)}
                                disabled={
                                  !canAddToSelectedPlaylist || isBusy || isRestrictedTrack
                                }
                                title={
                                  isRestrictedTrack
                                    ? `Restricted video (${restrictionReason})`
                                    : !selectedPlaylist
                                    ? "Select a playlist to add tracks"
                                    : isSpotifyProvider && !canEditSelectedPlaylist
                                      ? "This playlist is read-only"
                                      : `Add to ${selectedPlaylist.name}`
                                }
                                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[color:var(--session-accent-ink)] transition-[border-color,color,opacity,background] duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)] hover:border-[color:var(--session-accent-border)] hover:bg-[color:var(--session-accent-soft-strong)] hover:text-[color:var(--color-primary-foreground)] disabled:cursor-not-allowed disabled:opacity-50"
                                style={{
                                  borderColor: "var(--session-accent-border)",
                                  background:
                                    "color-mix(in srgb, var(--session-accent-soft) 90%, var(--surface-card) 10%)",
                                }}
                                aria-label={`Add ${track.name} to playlist`}
                              >
                                <Plus className="h-[0.875rem] w-[0.875rem]" />
                              </button>
                            )
                          ) : null}

                          <button
                            type="button"
                            onClick={() => handlePlayTrack(track)}
                            className="flex min-w-0 flex-1 flex-col border-0 bg-transparent p-0 text-left"
                            disabled={
                              isBusy || playbackBlockedBySensor || isRestrictedTrack
                            }
                            title={
                              isRestrictedTrack
                                ? `Restricted video (${restrictionReason})`
                                : `Play ${track.name}`
                            }
                          >
                            <span className="flex min-w-0 items-center gap-1.5">
                              <span
                                className={cn(
                                  "truncate text-[0.875rem] font-medium transition-colors duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)]",
                                  isCurrent
                                    ? "text-[color:var(--session-accent-ink)]"
                                    : "text-[color:var(--color-foreground)]"
                                )}
                              >
                                {track.name}
                              </span>
                              {isRestrictedTrack ? (
                                <span className="shrink-0 rounded-full bg-[color:var(--color-warning-soft)] px-1.5 py-0.5 text-[0.625rem] font-semibold text-[color:var(--color-warning-foreground)]">
                                  Restricted
                                </span>
                              ) : null}
                            </span>
                            <span
                              className={cn(
                                "truncate text-[0.6875rem] transition-colors duration-[var(--motion-duration-base)] ease-[var(--motion-ease-standard)]",
                                isCurrent
                                  ? "text-[color:color-mix(in_srgb,var(--session-accent-ink)_70%,var(--color-foreground)_30%)]"
                                  : "text-[color:var(--color-muted-foreground)]",
                              )}
                            >
                              {track.artists.map((a) => a.name).join(", ")}
                              {track.album ? ` • ${track.album.name}` : null}
                              {isRestrictedTrack
                                ? ` • ${restrictionReason}`
                                : null}
                            </span>
                          </button>
                        </div>
                      );
                    })}
              </div>
            </div>

            {results.length === SEARCH_LIMIT && (
              <div
                className="border-t px-2 py-1.5 text-center text-[0.625rem] text-[color:color-mix(in_oklch,var(--color-muted-foreground)_70%,transparent)]"
                style={{
                  borderColor:
                    "color-mix(in oklch, var(--color-border) 55%, transparent)",
                }}
              >
                Showing top {SEARCH_LIMIT} results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
